import { type ShortUrlResponse } from '@air/api/types';

import { type PublicBoardProps } from '~/components/Layouts/PublicBoardLayout/PublicBoard';
import { type PublicCanvasProps } from '~/components/Layouts/PublicCanvasLayout/PublicCanvas';
import { type PublicClipProps } from '~/components/Layouts/PublicClipLayout/PublicClip';
import { type PublicCollectionProps } from '~/components/Layouts/PublicCollectionLayout/PublicCollection';

export type PasscodeRequiredProps = {
  shortId: string;
  passcodeRequired: true;
  boardId: string | null;
  clipId: string | null;
};

export type NotFoundProps = {
  shortId: string | undefined;
  notFound: true;
  passcodeRequired?: false;
};

export type ShortUrlPageProps =
  | PublicBoardProps
  | PublicCanvasProps
  | PublicClipProps
  | PublicCollectionProps
  | PasscodeRequiredProps
  | NotFoundProps;

// feature checks
export const isPasscodeProtected = (props: ShortUrlPageProps): props is PasscodeRequiredProps =>
  'passcodeRequired' in props && props.passcodeRequired === true;

// route helpers
export const isBoardRoute = (props: ShortUrlPageProps): props is PublicBoardProps => !!props.shortId?.startsWith('b');

export const isClipRoute = (props: ShortUrlPageProps): props is PublicClipProps => !!props.shortId?.startsWith('c');

export const isCanvasRoute = (props: ShortUrlPageProps): props is PublicCanvasProps => !!props.shortId?.startsWith('v');

export const isCollectionRoute = (props: ShortUrlPageProps): props is PublicCollectionProps =>
  !!props.shortId?.startsWith('m');

export const isCollectionBoardRoute = (props: ShortUrlPageProps): props is PublicBoardProps =>
  isCollectionRoute(props) && typeof props.initialData === 'object' && 'board' in props.initialData;

export const getFullShareUrl = (id: ShortUrlResponse['id'], searchParams?: URLSearchParams) =>
  `${window.location.origin}/a/${id}${searchParams ? `?${searchParams.toString()}` : ''}`;

// state helpers
export const getInitialBoardData = (props: ShortUrlPageProps): PublicBoardProps['initialData'] | undefined => {
  if (isBoardRoute(props)) {
    return props.initialData;
  } else if (isCollectionBoardRoute(props)) {
    return props.initialData;
  } else {
    return undefined;
  }
};

export const getInitialClipData = (props: ShortUrlPageProps): PublicClipProps['initialData'] | undefined =>
  isClipRoute(props) ? props.initialData : undefined;

export const getInitialCanvasData = (props: ShortUrlPageProps): PublicCanvasProps['initialData'] | undefined =>
  isCanvasRoute(props) ? props.initialData : undefined;

export const getInitialCollectionData = (props: ShortUrlPageProps): PublicCollectionProps['initialData'] | undefined =>
  isCollectionRoute(props) ? props.initialData : undefined;
